import { Navigate, type RouteObject } from "infrastructure/router";
import { WebRoutes } from "consts/enpoints/web";
import { Filters as TransactionFilters } from "consts/transactions";
import { OnboardingApplication, OnboardingApplicationStep, Filters as OnboardingFilters } from "consts/onboarding";
import routes from "consts/routes";
import NotAuthorized from "ui/molecules/NotAuthorized";
import { Protected, RedirectIfAuthenticated } from "ui/layouts/Auth";
import Dashboard, { DashboardIndexRedirect } from "ui/layouts/Dashboard";
import { SignInOutlet } from "ui/layouts/SignIn";
import Login from "ui/screens/Login";
import Verify2FA from "ui/screens/Verify2FA";
import SendResetPasswordLink from "ui/screens/SendResetPasswordLink";
import ChangePassword from "ui/screens/ChangePassword";
import {
    KYCMerchantList,
    Merchants,
    TicketingSystem,
    TicketingSystemMerchantList
} from "ui/screens/Merchants";
import {
    KYCSettingsLayout,
    PSPKYCSettings
} from "ui/screens/KYCSettings";
import Transactions from "ui/screens/Transactions";
import TransactionList from "ui/screens/TransactionList";
import UserManagement from "ui/screens/UserManagement";
import MerchantPayouts from "ui/screens/MerchantPayouts";
import { QueryBuilder, EsTransactions, EsMerchantPayoutFiles } from "ui/screens/QueryBuilder";
import {
    BlacklistedConsumers,
    WhitelistedConsumers,
    ActiveConsumers,
    Consumers,
    ConsumersOutlet,
    ImportHistory
} from "ui/screens/Consumers";
import {
    PaymentMethods,
    GeneralSetups
} from "ui/screens/Setup";
import {
    BankingSDK,
    IbanXS,
    BankingSDKRecipients,
    BankingSDKConnectorsRecipientsPivot,
    IbanXSBanksRecipientsPivot,
    IbanXSBanksCountriesPivot
} from "ui/screens/OpenBanking";
import StatisticDashboard from "ui/screens/StatisticDashboard";
import Onboarding, { Applications, ECommerce, HighRisk } from "ui/screens/Onboarding";

export const config: RouteObject[] = [
    {
        path: WebRoutes.Root,
        element: (
            <Protected>
                <DashboardIndexRedirect>
                    <Dashboard />
                </DashboardIndexRedirect>
            </Protected>
        ),
        children: [
            {
                ...routes[WebRoutes.Dashboard],
                element: (
                    <StatisticDashboard
                        {...routes[WebRoutes.Dashboard]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Transactions],
                element: (
                    <Transactions
                        {...routes[WebRoutes.Transactions]}
                    />
                )
            },
            {
                ...routes[WebRoutes.TransactionList],
                element: (
                    <TransactionList
                        {...routes[WebRoutes.TransactionList]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Merchants.Index],
                element: (
                    <Navigate
                        replace
                        to={routes[WebRoutes.Merchants.List].path}
                    />
                )
            },
            {
                ...routes[WebRoutes.Merchants.List],
                element: (
                    <Merchants
                        {...routes[WebRoutes.Merchants.List]}
                    />
                )
            },
            {
                ...routes[WebRoutes.KYCSettings.Index],
                element: <KYCSettingsLayout />,
                children: [
                    {
                        index: true,
                        element: (
                            <Navigate
                                replace
                                to={routes[WebRoutes.KYCSettings.MerchantKYCSettings.Index].path}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.KYCSettings.MerchantKYCSettings.Index],
                        element: (
                            <KYCMerchantList
                                {...routes[WebRoutes.KYCSettings.MerchantKYCSettings.Index]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.KYCSettings.PSPKYCSettings.Index],
                        element: (
                            <PSPKYCSettings
                                {...routes[WebRoutes.KYCSettings.PSPKYCSettings.Index]}
                            />
                        )
                    }
                ]
            },
            {
                ...routes[WebRoutes.Merchants.TicketingSystem.Index],
                element: (
                    <TicketingSystemMerchantList
                        {...routes[WebRoutes.Merchants.TicketingSystem.Index]}
                    />
                ),
                children: [
                    {
                        path: `:${TransactionFilters.merchantId}`,
                        element: (
                            <TicketingSystem
                                {...routes[WebRoutes.Merchants.TicketingSystem.Index]}
                            />
                        )
                    }
                ]
            },
            {
                ...routes[WebRoutes.Consumers.Index],
                element: (
                    <ConsumersOutlet />
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <Navigate
                                replace
                                to={routes[WebRoutes.Consumers.List].path}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Consumers.List],
                        element: (
                            <Consumers
                                {...routes[WebRoutes.Consumers.List]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Consumers.Blacklisted],
                        element: (
                            <BlacklistedConsumers
                                {...routes[WebRoutes.Consumers.Blacklisted]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Consumers.WhiteListed],
                        element: (
                            <WhitelistedConsumers
                                {...routes[WebRoutes.Consumers.WhiteListed]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Consumers.Active],
                        element: (
                            <ActiveConsumers
                                {...routes[WebRoutes.Consumers.Active]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Consumers.ImportHistory.Index],
                        element: (
                            <ImportHistory
                                {...routes[WebRoutes.Consumers.ImportHistory.Index]}
                            />
                        )
                    }
                ]
            },
            {
                ...routes[WebRoutes.Users],
                element: (
                    <UserManagement
                        {...routes[WebRoutes.Users]}
                    />
                )
            },
            {
                ...routes[WebRoutes.MerchantPayouts],
                element: (
                    <MerchantPayouts
                        {...routes[WebRoutes.MerchantPayouts]}
                    />
                )
            },
            {
                element: (
                    <QueryBuilder
                        {...routes[WebRoutes.QueryBuilder.Index]}
                    />
                ),
                children: [
                    {
                        ...routes[WebRoutes.QueryBuilder.Index],
                        element: (
                            <Navigate
                                replace
                                to={routes[WebRoutes.QueryBuilder.EsTransactions].path}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.QueryBuilder.EsTransactions],
                        element: (
                            <EsTransactions
                                {...routes[WebRoutes.QueryBuilder.EsTransactions]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.QueryBuilder.EsMerchantPayoutFiles],
                        element: (
                            <EsMerchantPayoutFiles
                                {...routes[WebRoutes.QueryBuilder.EsMerchantPayoutFiles]}
                            />
                        )
                    }
                ]
            },
            {
                ...routes[WebRoutes.Setup.Index],
                element: (
                    <Navigate
                        replace
                        to={routes[WebRoutes.Setup.PaymentMethodSetup].path}
                    />
                )
            },
            {
                ...routes[WebRoutes.Setup.PaymentMethodSetup],
                element: (
                    <PaymentMethods
                        {...routes[WebRoutes.Setup.PaymentMethodSetup]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Setup.GeneralSetup],
                element: (
                    <GeneralSetups
                        {...routes[WebRoutes.Setup.GeneralSetup]}
                    />
                )
            },
            {
                ...routes[WebRoutes.OpenBanking.Index],
                element: (
                    <Navigate
                        replace
                        to={routes[WebRoutes.OpenBanking.BankingSDK].path}
                    />
                )
            },
            {
                ...routes[WebRoutes.OpenBanking.BankingSDK],
                element: (
                    <BankingSDK
                        {...routes[WebRoutes.OpenBanking.BankingSDK]}
                    />
                )
            },
            {
                ...routes[WebRoutes.OpenBanking.IbanXS],
                element: (
                    <IbanXS
                        {...routes[WebRoutes.OpenBanking.IbanXS]}
                    />
                )
            },
            {
                ...routes[WebRoutes.OpenBanking.BankingSDKRecipients],
                element: (
                    <BankingSDKRecipients
                        {...routes[WebRoutes.OpenBanking.BankingSDKRecipients]}
                    />
                )
            },
            {
                ...routes[WebRoutes.OpenBanking.BankingSDKConnectorsRecipientsPivots],
                element: (
                    <BankingSDKConnectorsRecipientsPivot
                        {...routes[WebRoutes.OpenBanking.BankingSDKConnectorsRecipientsPivots]}
                    />
                )
            },
            {
                ...routes[WebRoutes.OpenBanking.IbanXSBanksRecipientsPivots],
                element: (
                    <IbanXSBanksRecipientsPivot
                        {...routes[WebRoutes.OpenBanking.IbanXSBanksRecipientsPivots]}
                    />
                )
            },
            {
                ...routes[WebRoutes.OpenBanking.IbanXSBanksCountriesPivots],
                element: (
                    <IbanXSBanksCountriesPivot
                        {...routes[WebRoutes.OpenBanking.IbanXSBanksCountriesPivots]}
                    />
                )
            },
            {
                ...routes[WebRoutes.Onboarding.Index],
                element: (
                    <Onboarding
                        {...routes[WebRoutes.Onboarding.Index]}
                    />
                ),
                children: [
                    {
                        ...routes[WebRoutes.Onboarding.Applications.Index],
                        element: (
                            <Applications
                                {...routes[WebRoutes.Onboarding.Applications.Index]}
                            />
                        )
                    },
                    {
                        ...routes[WebRoutes.Onboarding.Applications.ECommerce],
                        element: (
                            <ECommerce.ECommerceApplicataions
                                {...routes[WebRoutes.Onboarding.Applications.ECommerce]}
                            />
                        ),
                        children: [
                            {
                                path: `:${OnboardingFilters.pki}`,
                                element: (
                                    <ECommerce.ECommerceApplication />
                                ),
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate
                                                to={`${OnboardingApplicationStep[OnboardingApplication.ECommerce].BusinessInformation}`}
                                                replace
                                            />
                                        )
                                    },
                                    {
                                        path: `${OnboardingApplicationStep[OnboardingApplication.ECommerce].BusinessInformation}`,
                                        element: (
                                            <ECommerce.BusinessInformation />
                                        )
                                    },
                                    {
                                        path: `${OnboardingApplicationStep[OnboardingApplication.ECommerce].BankingAndFinance}`,
                                        element: (
                                            <ECommerce.BankingAndFinance />
                                        )
                                    },
                                    {
                                        path: `${OnboardingApplicationStep[OnboardingApplication.ECommerce].MediaAndComplianceCheck}`,
                                        element: (
                                            <ECommerce.MediaAndComplianceCheck />
                                        )
                                    },
                                    {
                                        path: `${OnboardingApplicationStep[OnboardingApplication.ECommerce].UBOIdentityVerification}`,
                                        element: (
                                            <ECommerce.UBOIdentityVerification />
                                        )
                                    },
                                    {
                                        path: `${OnboardingApplicationStep[OnboardingApplication.ECommerce].PaymentMethods}`,
                                        element: (
                                            <ECommerce.PaymentMethods />
                                        )
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        ...routes[WebRoutes.Onboarding.Applications.HighRisk],
                        element: (
                            <HighRisk.HighRiskApplications
                                {...routes[WebRoutes.Onboarding.Applications.HighRisk]}
                            />
                        )
                    }
                ]
            },
            {
                path: "*",
                element: (
                    <NotAuthorized />
                )
            }
        ]
    },
    {
        path: WebRoutes.Login.Index,
        element: (
            <RedirectIfAuthenticated>
                <SignInOutlet />
            </RedirectIfAuthenticated>
        ),
        children: [
            {
                index: true,
                element: (
                    <Login />
                )
            },
            {
                path: WebRoutes.Login.Verify2FA,
                element: (
                    <Verify2FA />
                )
            },
            {
                path: WebRoutes.Login.SendResetLink,
                element: (
                    <SendResetPasswordLink />
                )
            }
        ]
    },
    {
        path: WebRoutes.ChangePassword,
        element: (
            <RedirectIfAuthenticated>
                <SignInOutlet />
            </RedirectIfAuthenticated>
        ),
        children: [
            {
                index: true,
                element: (
                    <ChangePassword />
                )
            }
        ]
    }
];

export default config;
